import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, LogIn, CalendarCheck2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const AuBadge = () => (
  <span className="rise inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/80 py-1 pl-1 pr-4 text-xs font-semibold text-[#0B2D5B] shadow-sm backdrop-blur">
    <span className="grid h-6 w-6 place-items-center rounded-full bg-gradient-to-br from-[#00C16B] to-[#FF8A00] text-[10px] font-extrabold text-white">AU</span>
    Proudly built for Aussie workplaces
  </span>
);

const Headline = () => (
  <h1 className="rise rise-1 mt-7 text-5xl font-extrabold leading-[0.98] tracking-tight text-[#0B2D5B] sm:text-6xl lg:text-7xl">
    Your Team.<br />Your Time.<br />
    <span className="relative inline-block text-[#00A85C]">
      All in One Place.
      <svg className="absolute -bottom-3 left-0 w-full" viewBox="0 0 300 14" fill="none" preserveAspectRatio="none"><path d="M2 10C70 3 160 1 298 7" stroke="#FF8A00" strokeWidth="5" strokeLinecap="round" /></svg>
    </span>
  </h1>
);

const ClockCard = () => (
  <div className="floaty absolute -left-6 top-10 w-56 rounded-3xl border border-white/60 bg-white/90 p-4 shadow-2xl backdrop-blur-xl sm:-left-16" data-testid="hero-clock-card">
    <div className="flex items-center justify-between"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Shift</span><span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />Clocked in</span></div>
    <p className="font-mono-num mt-2 text-3xl font-bold text-[#0B2D5B]">04:22:18</p>
    <div className="mt-3 flex h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 text-sm font-bold text-white"><LogIn className="h-4 w-4 rotate-180" />Clock out</div>
  </div>
);

const GeoCard = () => (
  <div className="floaty-2 absolute -right-4 top-1/2 w-52 rounded-3xl border border-white/60 bg-white/90 p-4 shadow-2xl backdrop-blur-xl sm:-right-10">
    <div className="relative mx-auto grid h-20 w-20 place-items-center">
      <span className="radar absolute inset-0 rounded-full bg-emerald-400/40" /><span className="radar-2 absolute inset-0 rounded-full bg-emerald-400/40" />
      <span className="relative grid h-9 w-9 place-items-center rounded-full bg-[#00C16B] text-white"><MapPin className="h-4 w-4" /></span>
    </div>
    <p className="mt-2 text-center text-sm font-semibold text-[#0B2D5B]">Inside geofence</p>
    <p className="text-center text-xs text-slate-500">24 m from Bondi Café · 150 m zone</p>
  </div>
);

const RosterCard = () => (
  <div className="floaty-3 absolute -bottom-8 left-6 w-64 rounded-3xl border border-white/60 bg-[#0B2D5B]/95 p-4 text-white shadow-2xl backdrop-blur-xl">
    <div className="flex items-center gap-2 text-xs font-semibold text-amber-300"><CalendarCheck2 className="h-4 w-4" />Roster published</div>
    <div className="mt-3 grid grid-cols-5 gap-1.5">
      {["M", "T", "W", "T", "F"].map((d, i) => (
        <div key={i} className="text-center"><p className="text-[10px] text-white/50">{d}</p><div className={`mt-1 h-8 rounded-lg ${i === 2 ? "bg-amber-400" : "bg-[#00C389]/80"}`} /></div>
      ))}
    </div>
  </div>
);

export const HomeHero = () => (
  <section className="relative overflow-hidden bg-[#FBFAF6]" data-testid="home-hero">
    <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-emerald-200/50 blur-[120px]" />
    <div className="pointer-events-none absolute -right-20 -top-32 h-[480px] w-[480px] rounded-full bg-amber-200/60 blur-[120px]" />
    <div className="dot-bg pointer-events-none absolute inset-0 opacity-50" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 pb-24 pt-14 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pb-32 lg:pt-20">
      <div className="lg:col-span-6">
        <AuBadge />
        <Headline />
        <p className="rise rise-2 mt-8 max-w-xl text-lg text-slate-600">Time clocks, GPS geofencing, rosters, timesheets and team announcements — in one easy app your crew will actually love using. From the café to the construction site.</p>
        <div className="rise rise-3 mt-9 flex flex-wrap gap-3">
          <Link to="/register"><Button size="lg" className="group h-14 rounded-full bg-[#00C16B] px-8 text-base shadow-lg shadow-emerald-600/25 transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#00A85C]" data-testid="hero-start-trial-btn">Start your free trial <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" /></Button></Link>
          <Link to="/features"><Button size="lg" variant="outline" className="h-14 rounded-full border-[#0B2D5B]/20 bg-white px-8 text-base text-[#0B2D5B] hover:bg-white" data-testid="hero-features-btn"><Sparkles className="mr-1.5 h-4 w-4 text-[#FF8A00]" />See how it works</Button></Link>
        </div>
        <ul className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-slate-600">
          {["Set up in minutes", "Every Aussie time zone", "ABN-ready onboarding"].map((t) => <li key={t} className="flex items-center gap-1.5"><span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100"><Check className="h-3 w-3 text-[#00A85C]" /></span>{t}</li>)}
        </ul>
      </div>
      <div className="relative mx-auto w-full max-w-md lg:col-span-6 lg:max-w-none">
        <div className="relative ml-auto aspect-[4/5] w-[85%] overflow-hidden rounded-[2.5rem] shadow-[0_40px_80px_-20px_rgba(11,42,91,0.45)] ring-8 ring-white">
          <img src="/img/hero-cafe.jpg" alt="Barista checking her shift on Clockaroo" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B2D5B]/40 via-transparent" />
        </div>
        <ClockCard /><GeoCard /><RosterCard />
      </div>
    </div>
  </section>
);
